import React, { useState, useEffect, useRef} from "react";
import { useNavigate,Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { api } from '../../api';
import { useQuery } from "../hooks/useQuery";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import { customStyles } from "../../styles/dataTableStyles";
import { decimalTo } from '../../utils/utils'; 

export default function Orders(props) {
  // const today = new Date().toLocaleDateString("en-ca");
  // const { addAccess } = props;
  const token = useSelector((state) => state.auth.token);
  const { userType: role } = useSelector((state) => state.auth.user);
  const query = useQuery();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalRows, setTotalRows] = useState(0);
  const [search, setSearch] = useState("");

  // Get current month start and end
  const getDefaultDateRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toLocaleDateString("en-ca");
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toLocaleDateString("en-ca");
    return { firstDay, lastDay };
  };

  const { firstDay, lastDay } = getDefaultDateRange();
  const ordrStartDt = query.get("startDate");
  const ordrEndDt = query.get("endDate");

  const [startDate, setStartDate] = useState(ordrStartDt || firstDay);
  const [endDate, setEndDate] = useState(ordrEndDt || lastDay);
  const [cartData, setCartData] = useState("");
  const [orderList, setOrderList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadData, setDownloadData] = useState([]);
  const downloadRef = useRef();

  useEffect(() => {
    if (role === 3) {
      api.orders.getCarts({},token).then((result) => {
        if (result.success) {
          setCartData(result.data);
        }
      });
    }

    return () => {
      localStorage.removeItem("dateChanged");
    };
  }, [role,token]);

  useEffect(() => {

      setPage(1);
    // eslint-disable-next-line
  }, [search]);

  // Load default orders for current month
  useEffect(() => {
    if (!ordrStartDt && !ordrEndDt) {
      handleClick();
    }
    // eslint-disable-next-line
  }, [page,pageSize,token,search]);



  const handleCart = () => {
    if (cartData.length > 0) {
      navigate(`/orders-details`);
    } else {
      if (role === 3) {
        api.orders.getCarts({}, token).then((result) => {
          if (result.success) {
            setCartData(result.data);
          } else {
            toast.error(result.message);
          }
        });
      }
    }
  };

  const handleClick = async () => {
    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);
  
    if (startDateObj <= endDateObj) {
      setIsLoading(true);
  
      const formBody = {
        startDate: startDate,
        endDate: endDate,
        pageSize:pageSize,
        pageNo: page,
        searchKey:search
      };
  
      try {
        const res = await api.orders.getOrdersData(formBody, token);
  
        if (res.success && Array.isArray(res.data?.result)) {
          setOrderList(res.data.result);
          setDownloadData(res.data.result);
          setTotalRows(res.data.totalCount);
        } else {
          setOrderList([]);
          setDownloadData([]);
          setTotalRows(0);
          toast.error(res.message || "No orders found or unexpected response format.");
        }
      } catch (err) {
        setOrderList([]);
        setDownloadData([]);
        setTotalRows(0);
        console.error("Error fetching orders:", err);
        toast.error("An error occurred while fetching orders.");
      } finally {
        setIsLoading(false);
      }
    } else {
      toast.error("Start date must be smaller than end date");
    }
  };

    const downloadClick = async () => {
    setIsLoading(true);

    const formBody = {
      startDate: startDate,
      endDate: endDate,
      searchKey:search
    };

    try {
      const res = await api.orders.exportAllOrders(formBody, token);

      const data = res?.data?.length > 0 ? res.data : [];
      const csvArray = prepareCsvData(data);
      setDownloadData(csvArray);

      // Delay to wait for state update before clicking download
      setTimeout(() => {
        if (downloadRef.current) {
          downloadRef.current.link.click();
        }
      }, 100);
    } catch (err) {
      console.error("Error while downloading:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const prepareCsvData = (values) => {
    const csvHeaders = [
      "Id",
      "OrderDate",
      "Merchant Name",
      "Order ID",
      "Order Email ID",
      "Products",
      "State",
      "Total Quantity",
      "Total Amount",
      "Total D.MRP",
      "Discount Amount",
      "Net Amount",
      "Comment",
      "Status",
    ];
    const csvArray = [csvHeaders];

    for (let i = 0; i < values.length; i++) {
      const dateObj = new Date(values[i]?.orderDate);
      const formattedDate = `${dateObj.getDate().toString().padStart(2, "0")}-${(dateObj.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${dateObj.getFullYear()}`;

      csvArray.push([
        `${values[i]?.orderId}` || "NA",
        `${formattedDate}` || "NA",
        `${values[i]?.merchentName}` || "NA",
        `${values[i]?.orderNo}` || "NA",
        `${values[i]?.orderEmailId}` || "NA",
        `${values[i]?.ordersItems}` || "NA",
        `${values[i]?.paymentZone}` || "NA",
        `${values[i]?.ordersQuantity}` || "NA",
        `${values[i]?.orderAmount}` || "NA",
        `${values[i]?.vendorAmount}` || "NA",
        `${values[i]?.savingAmount ?? 0}`,
        `${values[i]?.netAmount ?? 0}`,
        `${values[i]?.comment}` || "NA",
        `${values[i]?.status}` || "NA",
      ]);
    }

    return csvArray;
  };

  


  const viewOrder = (e, rowId) => {
    e.stopPropagation();
    navigate(`/orders-info/${rowId}`);
  };

  const dataColumns = [
    {
      name: "ID",
      selector: (row) => row.orderId,
      cell: (row) => (
        <>
          <input type="hidden" value={row.orderId} />
          <span>{row.orderId}</span>
        </>
      ),
      sortable: false,
      width: "80px",
    },
    {
      name: "Order Date",
      selector: (row) => row.orderDate,
      cell: (row) => {
        const date = new Date(row.orderDate);
        return <span>{date.toLocaleString()}</span>; // shows date and time
      },
      sortable: false,
      width: "100px",
    },
    {
      name: "Merchant",
      selector: (row) => row.merchentName,
      cell: (row) => <span className="add-ellip">{row.merchentName}</span>,
    },
    {
      name: "Order ID",
      selector: (row) => row.orderNo,
      sortable: false,
    },
    {
      name: "Products",
      selector: (row) => row.ordersItems,
      cell: (row) => <span className="add-ellip">{row.ordersItems}</span>,
    },
    {
      name: "State",
      selector: (row) => row.paymentZone,
      width: "80px",
    },
    {
      name: "Qty",
      selector: (row) => row.ordersQuantity,
      width: "60px",
    },
    {
      name: "Total Amount",
      selector: (row) => row.orderAmount,
      cell: (row) => <span>{decimalTo(row.orderAmount).toFixed(2)}</span>,
    },
    {
      name: "D.MRP",
      selector: (row) => row.vendorAmount,
      cell: (row) => <span>{decimalTo(row.vendorAmount).toFixed(2)}</span>,
      width: "80px",
    },
    {
      name: "Margin",
      selector: (row) => row.savingAmount,
      cell: (row) => <span>{decimalTo(row.savingAmount).toFixed(2)}</span>,
      width: "80px",
    },
    {
       name: "Net Amount",
      selector: (row) => row.netAmount, // Optional, used for sorting/filtering
      cell: (row) => (
        <span>{decimalTo(row.netAmount).toFixed(2)}</span>
      ),
      width: "80px",
     
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => <span>{row.status || "N/A"}</span>,
      width: "100px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <button
          className="btn btn-sm btn-info px-3"
          onClick={(e) => viewOrder(e, row.orderId)}
        >
          View
        </button>
      ),
      ignoreRowClick: true,
      width: "90px",
    }
  ];

  return (
    <div className="container-fluid mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="sec-header">ORDERS</h2>
      </div>
      <div className="d-flex justify-content-end align-items-center mb-3 flex-wrap gap-2">

        <div className="d-flex align-items-center">
          <label className="mr-2">Start Date:</label>
          <input
            type="date"
            value={startDate}
            className="form-control"
            style={{ width: "200px", marginRight: "10px" }}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <label className="mr-2">End Date:</label>
          <input
            type="date"
            value={endDate}
            className="form-control"
            style={{ width: "200px", marginRight: "10px" }}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <button className="btn btn-primary ml-2 px-4" onClick={handleClick}>
            Get Orders
          </button>

            <button
          className="btn btn-primary ml-2 px-4"
          onClick={downloadClick}
          disabled={isLoading}
        >
          {isLoading ? "Preparing..." : "Download"}
        </button>

        {/* Hidden CSVLink for programmatic download */}
        <CSVLink
          data={downloadData}
          filename={`orders_${startDate}_to_${endDate}.csv`}
          className="d-none"
          target="_blank"
          ref={downloadRef}
        />
          {role === 3 && (
          <Link to="/add-orders" className="btn btn-success ml-2 px-4">
          Place Order
          </Link>
          )}

          {role === 3 && (
              <span className="view-btn" onClick={handleCart}>
                <i
                  className="bx bx-cart float-right bold dropdown-toggle p-2"
                  style={{ position: "relative", marginRight: "30px" }}
                >
                  <span
                    style={{
                      position: "absolute",
                      top: "-15px",
                      left: "18px",
                      background: "#5C2E91",
                      padding: "5px",
                      borderRadius: "50%",
                      lineHeight: "8px",
                      color: "#fff",
                      width: "30px",
                      height: "30px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "14px",
                    }}
                  >
                    {cartData.length}
                  </span>
                </i>
              </span>
            )}


         
        </div>
      
      </div>

         <div className="d-flex justify-content-end">
           <input
            type="text"
            placeholder="Search"
            className="form-control"
            style={{ width: "200px" , marginBottom: "10px" }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
      

         </div>

      <DataTable
        columns={dataColumns}
        data={orderList}
        progressPending={isLoading}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        onChangePage={setPage}
        paginationPerPage={pageSize}
        onChangeRowsPerPage={(newPageSize, newPage) => {
          setPageSize(newPageSize);
          setPage(newPage);
        }}
        paginationRowsPerPageOptions={[50, 100,150,200]}
        highlightOnHover
        customStyles={customStyles}
        responsive
        noHeader={false}
        noTableHead={false}
        wrap
/>
    </div>
  );
}
